import React, { useState } from 'react'
import { Modal, Steps, App } from 'antd'
import { useIntl } from 'react-intl'
import IntlMessages from '../../../util/IntlMessages'
import { WelcomeStep } from './steps/WelcomeStep'
import { IntegrationStep } from './steps/IntegrationStep'
import * as logger from '../../../services/logger'
import Api from '@vacationtracker/shared/services/api'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectAuthCompanySlice, setAuthCompany } from '../../../store/auth-company-slice'
import { ICompanyPlatformChangedEventBody } from '../../../routes/MigrationPage/types'
import { getTokensForPlatform } from '../../../routes/MigrationPage/util'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { calculateNoOfUsersForImport, UsersStep } from './steps/UsersStep'
import { LOADING_COMPLETED_KEY, SummaryStep } from './steps/SummaryStep'
import { Platform } from '@vacationtracker/shared/types/core-event'

export interface OnboardingADPStepDescriptor {
  title: React.ReactNode
  key: string
  completedPercentage: number
}

interface OnboardingADP {
  visible: boolean
  completedPercentage: number
}

export const OnboardingADP: React.FC<OnboardingADP> = ({
  visible,
  completedPercentage,
}: OnboardingADP) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [isLaunching, setIsLaunching] = useState(false)
  const [isFading, setIsFading] = useState(false)

  const steps: OnboardingADPStepDescriptor[] = [
    {
      title: <IntlMessages id="onboarding.adp.steps.welcome" />,
      key: 'welcome',
      completedPercentage: 10,
    },
    {
      title: <IntlMessages id="onboarding.adp.steps.integration" />,
      key: 'integration',
      completedPercentage: 20,
    },
    {
      title: <IntlMessages id="onboarding.adp.steps.users" />,
      key: 'users',
      completedPercentage: 40,
    },
    {
      title: <IntlMessages id="onboarding.adp.steps.summary" />,
      key: 'summary',
      completedPercentage: 100,
    },
  ]

  const getInitialStep = () => {
    if (completedPercentage >= 100) {
      return steps.length - 1
    }
    for (let i = 0; i < steps.length; i++) {
      if (completedPercentage < steps[i].completedPercentage) {
        return i
      }
    }
    return 0
  }

  const [currentStep, setCurrentStep] = useState(getInitialStep())
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
  const [connectedPlatform, setConnectedPlatform] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [adpUsersCount, setAdpUsersCount] = useState<number>(0)

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1
      const completedStepPercentage = steps[currentStep].completedPercentage
      setCurrentStep(nextStep)
      await sendProgressUpdateEvent(completedStepPercentage)
    }
  }

  const sendProgressUpdateEvent = async (newPercentage: number) => {
    try {
      const event = {
        eventType: 'COMPANY_SETTINGS_UPDATED',
        eventGroup: 'COMPANY',
        companyId: authCompany?.id,
        name: authCompany?.name,
        contactEmail: authCompany?.contactEmail,
        userEndDate: authCompany?.userEndDate,
        integrationOnboardings: authCompany?.integrationOnboardings?.map(onboarding => ({
          ...onboarding,
          completedPercentage: onboarding.name === 'ADP' ? newPercentage : onboarding.completedPercentage,
        })),
      }
  
      await Api.post('/core/event', event)
    } catch (error) {
      notification.error({
        message: formatMessage({ id: 'connect.platformErrorTitle' }),
        description: formatMessage({ id: 'connect.platformErrorDescription' }),
        duration: 10,
      })
      throw error
    }
  }

  const handleSkipForNow = async () => {
    await handleNext()
  }

  const handlePlatformSelect = (platformKey: string) => {
    setSelectedPlatform(platformKey)
  }

  const handleLoginStart = () => {
    setIsLoggingIn(true)
  }

  const handleLaunch = async () => {
    setIsFading(true)
    await sendProgressUpdateEvent(100)
    localStorage.removeItem('vt-onboarding-platform-token')
    localStorage.removeItem(LOADING_COMPLETED_KEY)
    
    setTimeout(() => {
      authCompany && dispatch(setAuthCompany({
        ...authCompany,
        integrationOnboardings: undefined,
      }))
      setIsLaunching(true)
    }, 800) //smooth fade out
  }

  const sendPlatformChangeEvent = async (platform: string, organizationId: string, thirdPartyId: string, newEmail?: string) => {
    try {  
      const event: ICompanyPlatformChangedEventBody = {
        eventType: 'COMPANY_PLATFORM_CHANGED',
        eventGroup: 'COMPANY',
        newPlatform: platform,
        oldPlatform: authCompany?.platform as PlatformEnum,
        userMappings: {
          [authUser.id]: {
            id: thirdPartyId,
            displayName: authUser.name,
            mail: newEmail || authUser.email,
            isAdmin: authUser.role === 'Admin',
            initialUser: authUser.initialUser,
          },
        },
        organizationId,
        slackOrgTeamId: platform === 'slack' ? organizationId : undefined,
        deleteUnmapped: false,
        token: getTokensForPlatform(platform as PlatformEnum),
      }
      authCompany && dispatch(setAuthCompany({ ...authCompany, platform: platform as Platform }))
      await Api.post('/core/event', event)
    } catch (error) {
      notification.error({
        message: formatMessage({ id: 'connect.platformErrorTitle' }),
        description: formatMessage({ id: 'connect.platformErrorDescription' }),
        duration: 10,
      })
      throw error
    }
  }

  const handleConnectionSuccess = (platform: string, orgId: string, token: string, thirdPartyId: string, newEmail?: string) => {
    setConnectedPlatform(platform)
    setIsLoggingIn(false)
    localStorage.setItem('vt-onboarding-platform-token', token)

    void (async () => {
      try {
        await sendPlatformChangeEvent(platform, orgId, thirdPartyId, newEmail)

        if (currentStep === 1) {
          const integrationStepPercentage = steps[1].completedPercentage
          await sendProgressUpdateEvent(integrationStepPercentage)
        }
        
        notification.success({
          message: formatMessage({ id: 'connect.platformSuccessTitle' }),
          description: formatMessage({ id: 'connect.platformSuccessDescription' }),
          duration: 5,
        })
      } catch (error) {
        setConnectedPlatform(null)
      }
    })()
  }

  const handleConnectionError = (error: Error | unknown, platform: string) => {
    setIsLoggingIn(false)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    logger.error('Platform connection failed:', errorMessage, { platform })
    setConnectedPlatform(null)
    
    notification.error({
      message: formatMessage({ id: 'connect.platformErrorTitle' }),
      description: formatMessage({ id: 'connect.platformErrorDescription' }),
      duration: 10,
    })
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <WelcomeStep
            onNext={() => void handleNext()}
          />
        )
      case 1:
        return (
          <IntegrationStep
            selectedPlatform={selectedPlatform}
            connectedPlatform={connectedPlatform}
            isLoggingIn={isLoggingIn}
            onNext={() => void handleNext()}
            onSkipForNow={() => void handleSkipForNow()}
            onPlatformSelect={handlePlatformSelect}
            onConnectionSuccess={handleConnectionSuccess}
            onConnectionError={handleConnectionError}
            onLoginStart={handleLoginStart}
          />
        )
      case 2:
        return (
          <UsersStep
            onImport={() => void handleNext()}
            onSkip={() => {
              void handleSkipForNow()
              setAdpUsersCount(0)
              localStorage.setItem(LOADING_COMPLETED_KEY, 'true')
            }}
            onDataLoaded={(data) => setAdpUsersCount(calculateNoOfUsersForImport(data, authCompany?.platform as PlatformEnum))}
          />
        )
      case 3:
        return (
          <SummaryStep
            onLaunch={() => void handleLaunch()}
            usersCount={adpUsersCount}
            authCompanyPlatform={authCompany?.platform as PlatformEnum}
          />
        )
      default:
        return null
    }
  }

  return (
    <Modal
      title={null}
      open={visible && !isLaunching}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden={false}
      width={1200}
      centered
      className={`onboarding-adp-wizard ${isFading ? 'onboarding-adp-wizard-fading' : ''}`}
    >
      <div className="onboarding-adp-wizard-container">
        {/* Step indicator */}
        <div className="onboarding-adp-wizard-step-indicator">
          <Steps 
            current={currentStep} 
            size="small"
            items={steps.map((step, index) => ({
              title: (
                <span 
                  className={
                    index === currentStep ? 'step-title-current' :
                      index < currentStep ? 'step-title-completed' :
                        'step-title-pending'
                  }
                >
                  {step.title}
                </span>
              ),
              status: index === currentStep ? 'process' : index < currentStep ? 'finish' : 'wait',
            }))}
          />
        </div>
        <div className="onboarding-adp-wizard-step-content">
          {renderStepContent()}
        </div>
      </div>
    </Modal>
  )
}

export { OnboardingADPConnector } from './connector'
